import { type FilterScope, FilterScopes } from './filter-scope'
import type { FilterType } from './rules'

/**
 * What the "add filter" menu needs to know about a filter type.
 */
export interface FilterTypeInfo {
  type: FilterType
  friendlyName: string
  friendlyDescription: string
  hidden?: boolean
  /**
   * Scopes the filter needs. It is only offered where all are available.
   */
  scope?: readonly FilterScope[]
}

/**
 * The filter types offered in a scope, essentials first.
 */
export interface FilterTypeGroups {
  essentials: FilterTypeInfo[]
  others: FilterTypeInfo[]
}

/**
 * Filter types, as offered in the "add filter" menu. Essentials are listed
 * first.
 */
export const filterTypes: FilterTypeInfo[] = [
  {
    type: 'BinaryOperation',
    friendlyName: 'Binary Operation',
    friendlyDescription: 'Perform a binary operation on an answer.',
  },
  {
    type: 'Conjunction',
    friendlyName: 'Conjunction',
    friendlyDescription: 'Tests whether all/any of the subfilters pass.',
  },
  {
    type: 'Constant',
    friendlyName: 'Constant',
    friendlyDescription: 'Returns the specified value.',
  },
  {
    type: 'Expression',
    friendlyName: 'Formula',
    friendlyDescription: 'Write a formula that resolves to a yes/no.',
    hidden: true,
  },
  {
    type: 'HasSkills',
    friendlyName: 'Check Skills',
    friendlyDescription:
      'Determines whether or not a Pro has the specified skills.',
    scope: [FilterScopes.PROVIDER],
  },
  {
    type: 'If',
    friendlyName: 'If',
    friendlyDescription:
      'If (some filter) then (some other filter) else (some different filter)',
  },
]

const essentialTypes: FilterType[] = ['If', 'Conjunction', 'Constant']

/**
 * Filter types available in `scope`, split into essentials and others, each
 * sorted by name.
 *
 * @param scope
 */
export function groupFilterTypes(scope: readonly string[]): FilterTypeGroups {
  const available = filterTypes
    .filter((t) => !t.hidden)
    .filter((t) => !t.scope?.length || t.scope.every((s) => scope.includes(s)))
    .sort((a, b) => a.friendlyName.localeCompare(b.friendlyName))
  return {
    essentials: available.filter((t) => essentialTypes.includes(t.type)),
    others: available.filter((t) => !essentialTypes.includes(t.type)),
  }
}
